import {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { instance } from '~/utils/api/api';
import { AuthenticationContext } from '~/components/Authentication/AuthenticationProvider';
import notificationSound from '~/assets/sounds/notification.wav';
import {
  NotificationSettings,
  NotificationCounts,
  DEFAULT_NOTIFICATION_SETTINGS,
} from './types';
import { NotificationContext } from './NotificationContext';

// Интервал поллинга версии — 15 сек (1 Redis GET, ~1мс на бекенде)
const POLL_INTERVAL = 15_000;

const getSettingsKey = (userId: number) => `notification-settings-${userId}`;

const loadSettings = (userId: number): NotificationSettings => {
  try {
    const stored = localStorage.getItem(getSettingsKey(userId));
    if (stored) {
      return { ...DEFAULT_NOTIFICATION_SETTINGS, ...JSON.parse(stored) };
    }
  } catch {
    // ignore
  }
  return DEFAULT_NOTIFICATION_SETTINGS;
};

const saveSettings = (userId: number, settings: NotificationSettings) => {
  localStorage.setItem(getSettingsKey(userId), JSON.stringify(settings));
};

export const NotificationProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { user } = useContext(AuthenticationContext);
  const userId = user?.data?.id;
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const [settings, setSettings] = useState<NotificationSettings>(
    userId ? loadSettings(userId) : DEFAULT_NOTIFICATION_SETTINGS
  );

  const versionRef = useRef<number | null>(null);
  const prevCountsRef = useRef<NotificationCounts | null>(null);
  const originalTitleRef = useRef(document.title);
  const flashIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Инициализация аудио
  useEffect(() => {
    audioRef.current = new Audio(notificationSound);
    audioRef.current.volume = 0.5;
  }, []);

  // Загрузка настроек при смене пользователя
  useEffect(() => {
    if (userId) {
      setSettings(loadSettings(userId));
    }
  }, [userId]);

  const updateSettings = useCallback(
    (partial: Partial<NotificationSettings>) => {
      setSettings(prev => {
        const next = { ...prev, ...partial };
        if (userId) saveSettings(userId, next);
        return next;
      });
    },
    [userId]
  );

  // Мигание заголовка вкладки
  const startTitleFlash = useCallback((message: string) => {
    if (flashIntervalRef.current) return;
    const original = originalTitleRef.current;
    let isOriginal = true;
    flashIntervalRef.current = setInterval(() => {
      document.title = isOriginal ? message : original;
      isOriginal = !isOriginal;
    }, 1000);
  }, []);

  const stopTitleFlash = useCallback(() => {
    if (flashIntervalRef.current) {
      clearInterval(flashIntervalRef.current);
      flashIntervalRef.current = null;
      document.title = originalTitleRef.current;
    }
  }, []);

  // Остановка мигания при возврате на вкладку
  useEffect(() => {
    const handleVisibility = () => {
      if (!document.hidden) {
        stopTitleFlash();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibility);
  }, [stopTitleFlash]);

  // Определяем что изменилось и генерируем сообщения
  const detectChanges = useCallback(
    (prev: NotificationCounts, next: NotificationCounts): string[] => {
      const messages: string[] = [];
      const { sections } = settings;

      if (
        sections.tickets &&
        next.new_tickets_count > prev.new_tickets_count
      ) {
        messages.push(t('new_ticket_notification', 'Новая заявка'));
      }
      if (sections.orders && next.new_orders_count > prev.new_orders_count) {
        messages.push(t('new_order_notification', 'Новый заказ'));
      }
      if (
        sections.feedback &&
        next.new_feedbacks_count > prev.new_feedbacks_count
      ) {
        messages.push(t('new_feedback_notification', 'Новое обращение'));
      }
      if (
        sections.entertainment &&
        next.new_book_count > prev.new_book_count
      ) {
        messages.push(t('new_booking_notification', 'Новое бронирование'));
      }

      return messages;
    },
    [settings, t]
  );

  // Воспроизведение звука
  const playSound = useCallback(() => {
    if (settings.soundEnabled && audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {
        // Браузер может блокировать автовоспроизведение до взаимодействия
      });
    }
  }, [settings.soundEnabled]);

  // Показ уведомлений
  const showNotifications = useCallback(
    (messages: string[]) => {
      if (!settings.visualEnabled) return;
      messages.forEach(msg => {
        toast.info(msg, {
          position: 'top-right',
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
        });
      });
    },
    [settings.visualEnabled]
  );

  // Основной цикл поллинга
  useEffect(() => {
    if (!userId) return;

    const poll = async () => {
      try {
        // 1. Проверяем версию (лёгкий запрос)
        const { data: versionData } = await instance.get(
          'admin/notifications-version'
        );
        const newVersion = versionData.version;

        // Первый запрос — запоминаем версию и текущие counts
        if (versionRef.current === null) {
          versionRef.current = newVersion;
          try {
            const { data: counts } = await instance.get('admin/new-records');
            prevCountsRef.current = counts;
          } catch {
            // ignore
          }
          return;
        }

        // Версия не изменилась — ничего не делаем
        if (newVersion === versionRef.current) return;

        // 2. Версия изменилась — рефетчим тяжёлый endpoint
        versionRef.current = newVersion;

        // Инвалидируем кэш React Query — бейджи обновятся
        await queryClient.invalidateQueries({
          queryKey: ['notifications-count'],
        });

        // Получаем свежие counts для детекции изменений
        const { data: newCounts } = await instance.get('admin/new-records');

        if (prevCountsRef.current) {
          const messages = detectChanges(prevCountsRef.current, newCounts);

          if (messages.length > 0) {
            playSound();
            showNotifications(messages);

            if (document.hidden) {
              startTitleFlash(t('new_notification_title', 'Новое уведомление!'));
            }
          }
        }

        prevCountsRef.current = newCounts;
      } catch {
        // Ошибка сети — молча пропускаем, следующий poll продолжит работу
      }
    };

    poll();
    const interval = setInterval(poll, POLL_INTERVAL);

    return () => clearInterval(interval);
  }, [
    userId,
    queryClient,
    detectChanges,
    playSound,
    showNotifications,
    startTitleFlash,
    t,
  ]);

  return (
    <NotificationContext.Provider value={{ settings, updateSettings }}>
      {children}
    </NotificationContext.Provider>
  );
};
